// axiosCentral.js
import axios from "axios";
import { BASE_URL } from "./constants";
import { refreshAccessToken } from "../services/tokenService";
import { logoutUserDirect } from "../services/authRequests";
// Variables to handle concurrent refresh requests
let isRefreshing = false;
let failedQueue = [];

/**
 * Processes the queue of failed requests waiting for a new token.
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create a centralized Axios instance.
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
  // withCredentials is set per request in our helper functions.
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    //console.log("Sending request to:", config.url);
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor with token refresh and forced logout on failure.
// api.interceptors.response.use(
//   (response) => {
//     console.log("📥 Response received:", response.config.url);
//     return response;
//   },
//   async (error) => {
//     const originalRequest = error.config;

//     // Check for 401 errors that are not for the refresh endpoint
//     if (
//       error.response?.status === 401 &&
//       !originalRequest._retry &&
//       !originalRequest.url.includes("/auth/customer/refresh-token")
//     ) {
//       // If a refresh is already in progress, queue this request.
//       if (isRefreshing) {
//         return new Promise((resolve, reject) => {
//           failedQueue.push({ resolve, reject });
//         })
//           .then(() => {
//             // Remove any Authorization header (not used here) and retry.
//             delete originalRequest.headers["Authorization"];
//             return api(originalRequest);
//           })
//           .catch((err) => Promise.reject(err));
//       }

//       originalRequest._retry = true;
//       isRefreshing = true;

//       // Attempt token refresh.
//       return new Promise(async (resolve, reject) => {
//         try {
//           console.warn("⚠️ Access token expired. Attempting to refresh...");
//           const refreshData = await refreshAccessToken();
//           console.log("🔄 Token refreshed:", refreshData);

//           // Process queued requests.
//           processQueue(null, null);

//           // Remove any Authorization header (since we're using cookies).
//           delete originalRequest.headers["Authorization"];
//           resolve(api(originalRequest));
//         } catch (refreshError) {
//           console.error("❌ Token refresh failed:", refreshError);
//           processQueue(refreshError, null);

//           // Trigger forced logout by dispatching a custom event.
//           window.dispatchEvent(new CustomEvent("forceLogout"));
//           reject(refreshError);
//         } finally {
//           isRefreshing = false;
//         }
//       });
//     }

//     // For other errors, reject the promise.
//     console.error(
//       "API error:",
//       error.response?.status,
//       error.response?.data || error.message
//     );
//     return Promise.reject(error);
//   }
// );

// Response interceptor that forces logout on 401 errors.
api.interceptors.response.use(
  (response) => {
   // console.log("📥 Response received:", response.config.url);
    return response;
  },
  (error) => {
    // If the server returns a 401 error, force logout the user.
    if (error.response?.status === 401) {
      console.warn("Access token is invalid. Forcing logout...");
      logoutUserDirect();
    }
    
    console.error(
      "API error:",
      error.response?.status,
      error.response?.data || error.message
    );
    return Promise.reject(error);
  }
);

/**
 * Helper function to perform API requests.
 * The `requiresAuth` flag determines if `withCredentials` should be enabled.
 */
const apiRequest = async (method, endpoint, data = null, params = {}, requiresAuth = false) => {
  try {
    const config = {
      method,
      url: endpoint,
      data,
      params,
      withCredentials: requiresAuth,
    };
    const response = await api(config);
    return response.data;
  } catch (error) {
    console.error(
      "API error:",
      error.response?.status,
      error.response?.data || error.message
    );
    throw error;
  }
};

// Export helper functions for common HTTP methods.
export const getRequest = (endpoint, params = {}, requiresAuth = false) =>
  apiRequest("get", endpoint, null, params, requiresAuth);
export const postRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("post", endpoint, data, {}, requiresAuth);
export const putRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("put", endpoint, data, {}, requiresAuth);
export const deleteRequest = (endpoint, requiresAuth = false) =>
  apiRequest("delete", endpoint, null, {}, requiresAuth);
export const patchRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("patch", endpoint, data, {}, requiresAuth);

export default api;
